// Tenant brand colours -> the CSS variables behind tailwind's primary-* and
// accent-* scales (see tailwind.config.ts and the :root block in index.css).
//
// A tenant picks ONE primary and ONE secondary colour; the full 50-950 scale
// every existing component already uses is derived here, so no shared
// component ever needs to know a tenant's colours.

export type Rgb = [number, number, number];
export type Scale = Record<ScaleStep, string>;

export const SCALE_STEPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const;
export type ScaleStep = (typeof SCALE_STEPS)[number];

const HEX = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

export function isHexColor(value: string): boolean {
  return HEX.test(value.trim());
}

/** "#ABC" / "abc" / "#aabbcc" -> "#aabbcc"; anything else -> "". */
export function normalizeHex(value: string): string {
  const m = HEX.exec(value.trim());
  if (!m) return "";
  let hex = m[1]!.toLowerCase();
  if (hex.length === 3) hex = hex.split("").map((c) => c + c).join("");
  return `#${hex}`;
}

export function hexToRgb(hex: string): Rgb {
  const n = normalizeHex(hex).slice(1);
  return [parseInt(n.slice(0, 2), 16), parseInt(n.slice(2, 4), 16), parseInt(n.slice(4, 6), 16)];
}

export function rgbToHex([r, g, b]: Rgb): string {
  return `#${[r, g, b].map((c) => Math.round(c).toString(16).padStart(2, "0")).join("")}`;
}

function mix(a: Rgb, b: Rgb, amountOfB: number): Rgb {
  return [0, 1, 2].map((i) => a[i]! + (b[i]! - a[i]!) * amountOfB) as Rgb;
}

const WHITE: Rgb = [255, 255, 255];
const BLACK: Rgb = [0, 0, 0];

function luminance([r, g, b]: Rgb): number {
  const lin = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** WCAG contrast ratio between two colours. */
export function contrastRatio(a: Rgb, b: Rgb): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

/** Darkens c until white text on it reaches `target` contrast (AA = 4.5). */
export function darkenForWhiteText(c: Rgb, target = 4.5): Rgb {
  let out = c;
  for (let i = 0; i < 20 && contrastRatio(out, WHITE) < target; i++) {
    out = mix(out, BLACK, 0.08);
  }
  return out;
}

const channels = (c: Rgb) => c.map((v) => Math.round(v)).join(" ");

/**
 * Primary scale. 700 is the filled-button colour across the app, so the
 * tenant's colour lands there - darkened only as far as white button text
 * needs to stay readable (WCAG AA). Lighter steps mix toward white for
 * tints and selected states, darker ones toward black for pressed states.
 */
export function primaryScale(hex: string): Scale {
  const base = darkenForWhiteText(hexToRgb(hex));
  const towardWhite: Partial<Record<ScaleStep, number>> = {
    50: 0.95, 100: 0.88, 200: 0.72, 300: 0.55, 400: 0.32, 500: 0.16, 600: 0.08,
  };
  const towardBlack: Partial<Record<ScaleStep, number>> = { 800: 0.25, 900: 0.5, 950: 0.7 };
  return buildScale(base, 700, towardWhite, towardBlack);
}

/**
 * Accent (secondary) scale. 400 is the decorative fill the app uses for the
 * accent, so the tenant's colour lands there; 600+ is the text-on-white
 * range and is darkened until it passes AA.
 */
export function accentScale(hex: string): Scale {
  const base = hexToRgb(hex);
  const towardWhite: Partial<Record<ScaleStep, number>> = { 50: 0.93, 100: 0.85, 200: 0.62, 300: 0.35 };
  const towardBlack: Partial<Record<ScaleStep, number>> = {
    500: 0.15, 600: 0.3, 700: 0.42, 800: 0.55, 900: 0.66, 950: 0.76,
  };
  const scale = buildScale(base, 400, towardWhite, towardBlack);
  for (const step of [600, 700, 800, 900, 950] as const) {
    scale[step] = channels(darkenForWhiteText(scale[step].split(" ").map(Number) as Rgb));
  }
  return scale;
}

function buildScale(
  base: Rgb,
  baseStep: ScaleStep,
  towardWhite: Partial<Record<ScaleStep, number>>,
  towardBlack: Partial<Record<ScaleStep, number>>,
): Scale {
  const scale = {} as Scale;
  for (const step of SCALE_STEPS) {
    if (step === baseStep) scale[step] = channels(base);
    else if (towardWhite[step] !== undefined) scale[step] = channels(mix(base, WHITE, towardWhite[step]!));
    else scale[step] = channels(mix(base, BLACK, towardBlack[step] ?? 0));
  }
  return scale;
}

/** CSS custom properties for a primary/secondary pair (either may be ""). */
export function themeVariables(primary: string, secondary: string): Record<string, string> {
  const vars: Record<string, string> = {};
  if (isHexColor(primary)) {
    const scale = primaryScale(primary);
    for (const step of SCALE_STEPS) vars[`--color-primary-${step}`] = scale[step];
  }
  if (isHexColor(secondary)) {
    const scale = accentScale(secondary);
    for (const step of SCALE_STEPS) vars[`--color-accent-${step}`] = scale[step];
  }
  return vars;
}

const ALL_VARIABLES = SCALE_STEPS.flatMap((s) => [`--color-primary-${s}`, `--color-accent-${s}`]);

/**
 * Applies (or, for empty colours, removes) the tenant overrides on <html>.
 * Removing falls back to the stock values declared in index.css.
 */
export function applyThemeColors(primary: string, secondary: string, root: HTMLElement = document.documentElement) {
  const vars = themeVariables(primary, secondary);
  for (const name of ALL_VARIABLES) {
    if (vars[name]) root.style.setProperty(name, vars[name]!);
    else root.style.removeProperty(name);
  }
}

/** The resolved filled-button colour, for <meta name="theme-color">. */
export function primaryButtonHex(primary: string): string {
  return isHexColor(primary) ? rgbToHex(darkenForWhiteText(hexToRgb(primary))) : "";
}
